import type { MouseEvent, ReactNode } from 'react';
import { MailIcon, PencilIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, Card, Progress, SimpleTooltip } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { Crest } from '@/shared/components/Crest';
import { formatCompactMoney } from '@/shared/lib/format';
import { countryFlag, countryName } from '@/shared/lib/countries';
import type { AgencyClubDto } from '@/api/generated/model';
import { RelationshipDot, RelationshipTag } from './RelationshipTag';

export interface ClubCardProps {
  club: AgencyClubDto;
  onOpen: (club: AgencyClubDto) => void;
  /**
   * Omit both to render the card read-only. The transfer-windows screen shows
   * the same card purely as a view of a league's clubs, and a delete button
   * sitting in a report is an invitation to a mistake.
   */
  onEdit?: (club: AgencyClubDto) => void;
  onRemove?: (club: AgencyClubDto) => void;
}

/**
 * One club in the grid.
 *
 * Every figure below the header comes from `transferWindow`, which is null when
 * the club's league has no open or upcoming window on record. That is a normal
 * state, not an error — the whole block is replaced with a single line saying
 * so rather than rendering zeroes that read as "this club has no budget".
 *
 * The card is clickable, and the actions inside it are not: each stops the
 * event so that reaching for "Call" never opens the detail page instead.
 */
export function ClubCard({ club, onOpen, onEdit, onRemove }: ClubCardProps) {
  const { t } = useTranslation();

  const { club: details, transferWindow: window } = club;
  // Contacts arrive primary-first from the API, so the head of the list is the
  // primary contact whenever there is one.
  const primaryContact = club.contacts[0];
  const flag = countryFlag(details.country);

  const stopAnd = (action: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    action();
  };

  return (
    <Card
      role="link"
      tabIndex={0}
      onClick={() => onOpen(club)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen(club);
      }}
      className="h-full cursor-pointer gap-4 px-4 transition-shadow outline-none hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <div className="flex items-start gap-3">
        <Crest name={details.shortName ?? details.name} logo={details.logo} size={44} />

        <div className="grid min-w-0 flex-1 gap-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-[15px] font-semibold">{details.name}</span>
            <RelationshipDot value={club.relationship} />
          </div>
          <span className="truncate text-[13px] text-muted-foreground">
            {flag ? `${flag} ` : ''}
            {countryName(details.country)}
            {details.league ? ` · ${details.league.name}` : ''}
          </span>
          <div>
            <RelationshipTag value={club.relationship} />
          </div>
        </div>
      </div>

      {window ? (
        <>
          <div className="flex gap-2.5">
            <Metric label={t('clubs.card.deals')} value={window.dealsCount} />
            <Metric
              label={t('clubs.card.budget')}
              value={formatCompactMoney(window.budget, window.currency)}
            />
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{t('clubs.card.spendCapacity')}</span>
              <span className="tabular-nums">{Math.round(window.spendCapacity)}%</span>
            </div>
            <SimpleTooltip
              content={t('clubs.card.spendCapacityHint', {
                window: window.name,
                spent: formatCompactMoney(window.budgetSpent, window.currency),
                budget: formatCompactMoney(window.budget, window.currency),
              })}
            >
              <Progress value={window.spendCapacity} />
            </SimpleTooltip>
          </div>
        </>
      ) : (
        <p className="text-[13px] text-muted-foreground">{t('clubs.card.noWindow')}</p>
      )}

      <div className="grid gap-0.5">
        <span className="text-xs text-muted-foreground">{t('clubs.card.contact')}</span>
        <span className="truncate text-[13px]">
          {primaryContact?.name ?? t('clubs.card.noContact')}
        </span>
      </div>

      <div className="mt-auto flex gap-2">
        <ContactAction
          icon={<MailIcon />}
          label={t('clubs.card.email')}
          href={primaryContact?.email ? `mailto:${primaryContact.email}` : undefined}
          missingLabel={t('clubs.card.noEmail')}
        />
        <ContactAction
          icon={<PhoneIcon />}
          label={t('clubs.card.call')}
          href={primaryContact?.phone ? `tel:${primaryContact.phone}` : undefined}
          missingLabel={t('clubs.card.noPhone')}
        />
        {onEdit ? (
          <SimpleTooltip content={t('actions.edit')}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('actions.edit')}
              onClick={stopAnd(() => onEdit(club))}
            >
              <PencilIcon />
            </Button>
          </SimpleTooltip>
        ) : null}
        {onRemove ? (
          <SimpleTooltip content={t('clubs.card.remove')}>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              aria-label={t('clubs.card.remove')}
              onClick={stopAnd(() => onRemove(club))}
            >
              <Trash2Icon />
            </Button>
          </SimpleTooltip>
        ) : null}
      </div>
    </Card>
  );
}

/** One of the two boxed figures under the club header. */
function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="grid min-w-0 flex-1 gap-0.5 rounded-lg bg-muted/60 px-3 py-2.5">
      <span className="text-[11px] tracking-[0.04em] text-muted-foreground uppercase">{label}</span>
      <span className="truncate text-lg font-semibold tabular-nums">{value}</span>
    </div>
  );
}

/**
 * Email / Call.
 *
 * Both are disabled rather than hidden when the primary contact has no address
 * or number: a button that vanishes makes the cards different heights and gives
 * the user nothing to hover for an explanation. The tooltip carries the reason.
 */
function ContactAction({
  icon,
  label,
  href,
  missingLabel,
}: {
  icon: ReactNode;
  label: string;
  href: string | undefined;
  missingLabel: string;
}) {
  return (
    <SimpleTooltip content={href ? undefined : missingLabel}>
      <span className="min-w-0 flex-1" onClick={(event) => event.stopPropagation()}>
        {href ? (
          <Button variant="outline" size="sm" className="w-full" asChild>
            <a href={href} onClick={(event) => event.stopPropagation()}>
              {icon}
              {label}
            </a>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled
            onClick={(event) => event.stopPropagation()}
          >
            {icon}
            {label}
          </Button>
        )}
      </span>
    </SimpleTooltip>
  );
}
